import React, { useEffect, useState } from 'react';
import api from '../services/api';
import PositionsEditor from '../components/PositionsEditor';
import { getAllProducts } from '../services/ProductService';
import { getAllCompanies } from '../services/CompanyService';
import { getAllUsers } from '../services/UserService';
import { toAcquisitionPayload } from '../dto/AcquisitionDTO';
import {
    DEAL_TYPES,
    LEAD_SOURCES,
    STAGES,
    formatEur,
    positionsTotal,
} from '../utils/salesPipeline';
import '../styles/AcquisitionModal.css';
import { useToast } from '../context/UIContext';
import { extractApiError } from '../utils/apiError';

const EMPTY = {
    companyName: '', companyId: '', contactPerson: '', contactEmail: '', contactPhone: '',
    projectTitle: '', projectDescription: '', classification: '',
    expectedBudget: '', status: 'Lead', priority: 'Medium',
    estimatedCloseDate: '', offerAmount: '', offerDate: '', notes: '',
    dealType: 'project', lineItems: [],
    leadSource: '', nextFollowUpDate: '', winProbability: '', ownerUserId: '', lostReason: '',
};

export default function AcquisitionModal({ acquisition, onClose, onSave }) {
    const toast = useToast();
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [products, setProducts] = useState([]);
    const [productsError, setProductsError] = useState('');
    const [companies, setCompanies] = useState([]);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        getAllProducts()
            .then(setProducts)
            .catch(() => setProductsError('Produktkatalog konnte nicht geladen werden — freie Positionen sind weiterhin möglich.'));
        getAllCompanies().then(setCompanies).catch(() => {});
        getAllUsers().then(setUsers).catch(() => {});
    }, []);

    useEffect(() => {
        if (acquisition) {
            setForm({
                ...EMPTY,
                ...acquisition,
                companyId: acquisition.companyId || '',
                classification: acquisition.classification || '',
                notes: acquisition.notes || '',
                projectDescription: acquisition.projectDescription || '',
                leadSource: acquisition.leadSource || '',
                ownerUserId: acquisition.ownerUserId || '',
                lostReason: acquisition.lostReason || '',
                estimatedCloseDate: acquisition.estimatedCloseDate?.substring(0, 10) || '',
                offerDate: acquisition.offerDate?.substring(0, 10) || '',
                nextFollowUpDate: acquisition.nextFollowUpDate?.substring(0, 10) || '',
                expectedBudget: acquisition.expectedBudget ?? '',
                offerAmount: acquisition.offerAmount ?? '',
                winProbability: acquisition.winProbability ?? '',
                lineItems: (acquisition.lineItems || []).map(it => ({ sub_description: '', discount_percent: 0, ...it })),
            });
        } else {
            setForm(EMPTY);
        }
        setErrors({});
    }, [acquisition]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    /** Linking an existing customer fills the company and any contact field still empty. */
    const handleCompanyLink = (e) => {
        const id = e.target.value;
        const company = companies.find(c => c.id === id);
        setForm(prev => ({
            ...prev,
            companyId: id,
            companyName: company ? company.name : prev.companyName,
            contactPerson: prev.contactPerson || company?.contactPerson || '',
            contactEmail: prev.contactEmail || company?.email || '',
            contactPhone: prev.contactPhone || company?.phone || '',
        }));
        setErrors(prev => ({ ...prev, companyName: '' }));
    };

    const validate = () => {
        const e = {};
        if (!form.companyName.trim()) e.companyName = 'Pflichtfeld';
        if (!form.projectTitle.trim()) e.projectTitle = 'Pflichtfeld';
        if (!form.contactPerson.trim()) e.contactPerson = 'Pflichtfeld';
        if (form.expectedBudget !== '' && parseFloat(form.expectedBudget) < 0) e.expectedBudget = 'Budget darf nicht negativ sein.';
        if (form.offerAmount !== '' && parseFloat(form.offerAmount) < 0) e.offerAmount = 'Betrag darf nicht negativ sein.';
        if (form.winProbability !== '' && (parseFloat(form.winProbability) < 0 || parseFloat(form.winProbability) > 100)) {
            e.winProbability = 'Wert zwischen 0 und 100.';
        }
        const namelessPosition = form.lineItems.findIndex(
            it => !((it.description || '').trim()) && parseFloat(it.unit_price) > 0
        );
        if (namelessPosition >= 0) e.lineItems = `Position ${namelessPosition + 1} braucht eine Bezeichnung.`;
        return e;
    };

    const handleSubmit = () => {
        const e = validate();
        if (Object.keys(e).length > 0) { setErrors(e); return; }

        const payload = toAcquisitionPayload(form);
        const req = acquisition
            ? api.patch(`/ProjectAcquisition/${acquisition.id}`, payload)
            : api.post('/ProjectAcquisition', payload);

        setSaving(true);
        req.then(onSave)
            .catch(err => toast(extractApiError(err, 'Speichern fehlgeschlagen.')))
            .finally(() => setSaving(false));
    };

    const field = (name, label, type = 'text', required = false, extra = {}) => (
        <div className="form-field">
            <label className="form-label">
                {label}{required && <span className="form-required">*</span>}
            </label>
            <input
                type={type}
                name={name}
                value={form[name]}
                onChange={handleChange}
                className={`input${errors[name] ? ' input-error' : ''}`}
                placeholder={label}
                {...extra}
            />
            {errors[name] && <span className="form-error-msg">{errors[name]}</span>}
        </div>
    );

    const hasPositions = form.lineItems.length > 0;
    const stageDefault = STAGES.find(s => s.key === form.status)?.probability ?? 0;

    return (
        <div className="modal-overlay">
            <div className="modal modal-wide modal-acq">
                <div className="modal-header">
                    <h3>{acquisition ? 'Deal bearbeiten' : 'Neuer Deal'}</h3>
                    <button className="modal-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body">
                    <div className="form-grid-2">
                        {field('projectTitle', 'Titel des Deals', 'text', true)}
                        <div className="form-field">
                            <label className="form-label">Was wird verkauft?</label>
                            <select name="dealType" value={form.dealType} onChange={handleChange} className="input">
                                {DEAL_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="form-section-label">Kunde</div>
                    <div className="form-grid-2">
                        {field('companyName', 'Firma', 'text', true)}
                        <div className="form-field">
                            <label className="form-label">Bestandskunde verknüpfen</label>
                            <select value={form.companyId} onChange={handleCompanyLink} className="input">
                                <option value="">Kein Bestandskunde</option>
                                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="form-grid-3">
                        {field('contactPerson', 'Ansprechpartner', 'text', true)}
                        {field('contactEmail', 'E-Mail', 'email')}
                        {field('contactPhone', 'Telefon', 'tel')}
                    </div>

                    <div className="form-section-label">Akquise &amp; Prozess</div>
                    <div className="form-grid-3">
                        <div className="form-field">
                            <label className="form-label">Phase</label>
                            <select name="status" value={form.status} onChange={handleChange} className="input">
                                {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                        </div>
                        <div className="form-field">
                            <label className="form-label">Leadquelle</label>
                            <select name="leadSource" value={form.leadSource} onChange={handleChange} className="input">
                                <option value="">Nicht angegeben</option>
                                {LEAD_SOURCES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </select>
                        </div>
                        <div className="form-field">
                            <label className="form-label">Verantwortlich</label>
                            <select name="ownerUserId" value={form.ownerUserId} onChange={handleChange} className="input">
                                <option value="">Nicht zugewiesen</option>
                                {users.map(u => <option key={u.id} value={u.id}>{u.name || u.username}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="form-grid-3">
                        <div className="form-field">
                            <label className="form-label">Priorität</label>
                            <select name="priority" value={form.priority} onChange={handleChange} className="input">
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                            </select>
                        </div>
                        <div className="form-field">
                            <label className="form-label">Gewinnwahrscheinlichkeit (%)</label>
                            <input
                                type="number" name="winProbability" min={0} max={100} step={5}
                                value={form.winProbability} onChange={handleChange}
                                className={`input${errors.winProbability ? ' input-error' : ''}`}
                                placeholder={`Standard: ${stageDefault}`}
                            />
                            {errors.winProbability && <span className="form-error-msg">{errors.winProbability}</span>}
                        </div>
                        {field('nextFollowUpDate', 'Nächste Wiedervorlage', 'date')}
                    </div>
                    <div className="form-grid-2">
                        {field('estimatedCloseDate', 'Voraussichtlicher Abschluss', 'date')}
                        {field('classification', 'Klassifikation (z.B. Consulting)')}
                    </div>

                    <div className="form-section-label">Positionen</div>
                    <PositionsEditor
                        items={form.lineItems}
                        onChange={(lineItems) => {
                            setForm(prev => ({ ...prev, lineItems }));
                            if (errors.lineItems) setErrors(prev => ({ ...prev, lineItems: '' }));
                        }}
                        products={products}
                        productsError={productsError}
                    />
                    {errors.lineItems && <span className="form-error-msg">{errors.lineItems}</span>}

                    <div className="form-section-label">Wert &amp; Angebot</div>
                    <div className="form-grid-3">
                        <div className="form-field">
                            <label className="form-label">Erwartetes Budget (€)</label>
                            <input
                                type="number" name="expectedBudget" min={0}
                                value={hasPositions ? positionsTotal(form.lineItems) : form.expectedBudget}
                                onChange={handleChange}
                                disabled={hasPositions}
                                className={`input${errors.expectedBudget ? ' input-error' : ''}`}
                                placeholder="Erwartetes Budget (€)"
                            />
                            {hasPositions
                                ? <span className="acq-hint">Ergibt sich aus den Positionen ({formatEur(positionsTotal(form.lineItems), { decimals: 2 })}).</span>
                                : errors.expectedBudget && <span className="form-error-msg">{errors.expectedBudget}</span>}
                        </div>
                        {field('offerAmount', 'Angebotsbetrag (€)', 'number', false, { min: 0 })}
                        {field('offerDate', 'Angebotsdatum', 'date')}
                    </div>

                    <div className="form-section-label">Beschreibung &amp; Notizen</div>
                    <div className="form-field">
                        <label className="form-label">Beschreibung</label>
                        <textarea
                            name="projectDescription"
                            value={form.projectDescription}
                            onChange={handleChange}
                            className="input input-textarea"
                            placeholder="Bedarf, Ausgangslage, Entscheidungskriterien …"
                            rows={3}
                        />
                    </div>
                    <div className="form-field">
                        <label className="form-label">Notizen</label>
                        <textarea
                            name="notes"
                            value={form.notes}
                            onChange={handleChange}
                            className="input input-textarea"
                            placeholder="Interne Notizen"
                            rows={2}
                        />
                    </div>
                    {form.status === 'Lost' && (
                        <div className="form-field">
                            <label className="form-label">Verlustgrund</label>
                            <input
                                type="text" name="lostReason" value={form.lostReason} onChange={handleChange}
                                className="input" placeholder="z.B. Preis, Timing, Wettbewerber"
                            />
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="button" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Speichern…' : 'Speichern'}
                    </button>
                    <button className="button button-secondary" onClick={onClose}>Abbrechen</button>
                </div>
            </div>
        </div>
    );
}
